import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { apiClient } from '../../api/client';
import { businessProfilesApi } from '../../api/business-profiles';
import { radioApi } from '../../api/radio';

interface TvSession {
  sessionCode: string;
  deviceId?: string;
  deviceName?: string;
  profileSlug?: string;
  mood?: string;
  isPlaying: boolean;
  volume: number;
  currentTrackName?: string;
  isOnline: boolean;
  lastHeartbeat: string;
}

interface TvState {
  profileSlug?: string;
  mood?: string;
  isPlaying: boolean;
  volume: number;
  currentTrackId?: string;
  currentTrackName?: string;
  elapsed: number;
  duration: number;
  isOnline?: boolean;
}

interface BusinessProfile {
  _id: string;
  slug: string;
  displayName: string;
  icon?: string;
}

export default function TvRemotePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionCode: urlSessionCode } = useParams<{ sessionCode?: string }>();
  const { isAuthenticated } = useAuthStore();

  const [sessions, setSessions] = useState<TvSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<TvSession | null>(null);
  const [tvState, setTvState] = useState<TvState | null>(null);
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [moods, setMoods] = useState<string[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingMoods, setLoadingMoods] = useState(false);
  const [showMoodPicker, setShowMoodPicker] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDisconnectingRef = useRef(false);
  const [localVolume, setLocalVolume] = useState<number>(100);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
    loadProfiles();

    const interval = setInterval(loadSessions, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Auto-connect if sessionCode is in URL
  useEffect(() => {
    // Don't auto-connect if we're intentionally disconnecting
    if (isDisconnectingRef.current) {
      return;
    }
    if (urlSessionCode && sessions.length > 0 && !selectedSession) {
      const session = sessions.find(s => s.sessionCode === urlSessionCode);
      if (session) {
        connectToSession(session);
      }
    }
  }, [urlSessionCode, sessions, selectedSession]);

  const loadSessions = async () => {
    try {
      const response = await apiClient.get<TvSession[]>('/tv-sessions');
      setSessions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setLoading(false);
    }
  };

  const loadProfiles = async () => {
    try {
      const data = await businessProfilesApi.getAll({ active: true });
      setProfiles(data);
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }
  };

  const loadMoods = async (profileSlug: string) => {
    setLoadingMoods(true);
    try {
      const data = await radioApi.getAvailableMoods(profileSlug);
      setMoods(data.moods);
    } catch (error) {
      console.error('Failed to load moods:', error);
    } finally {
      setLoadingMoods(false);
    }
  };

  // Connect to session SSE
  const connectToSession = (session: TvSession) => {
    setSelectedSession(session);

    // Update URL to include session code (for page reload persistence)
    if (!urlSessionCode || urlSessionCode !== session.sessionCode) {
      navigate(`/tv/remote/${session.sessionCode}`, { replace: true });
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Get token from localStorage for SSE auth (EventSource doesn't support headers)
    const authToken = localStorage.getItem('authToken');
    const url = `/api/v1/tv-sessions/${session.sessionCode}/events/remote?token=${authToken}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'state') {
          // Merge with existing state to handle partial updates (e.g., just isOnline)
          setTvState(prev => {
            const { type, ...stateData } = data;
            return prev ? { ...prev, ...stateData } : stateData;
          });
          // Don't sync profile when mood picker is open - user is selecting
          // Sync volume only if user isn't currently dragging
          if (data.volume !== undefined && !volumeTimeoutRef.current) {
            setLocalVolume(data.volume);
          }
        }
      } catch (e) {
        console.error('Failed to parse SSE:', e);
      }
    };

    eventSource.onerror = () => {
      // Will auto-reconnect
    };

    eventSourceRef.current = eventSource;
  };

  // Disconnect from session
  const disconnectFromSession = () => {
    // Set flag to prevent auto-reconnect
    isDisconnectingRef.current = true;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear state and navigate
    setSelectedSession(null);
    setTvState(null);
    navigate('/tv/remote', { replace: true });

    // Reset flag after navigation completes
    setTimeout(() => {
      isDisconnectingRef.current = false;
    }, 100);
  };

  // Send command to TV
  const sendCommand = async (command: string, params?: any) => {
    if (!selectedSession) return;

    try {
      await apiClient.post(`/tv-sessions/${selectedSession.sessionCode}/command`, {
        command,
        ...params,
      });
    } catch (error) {
      console.error('Failed to send command:', error);
    }
  };

  const handlePlay = () => sendCommand('play');
  const handlePause = () => sendCommand('pause');

  // Debounced volume change
  const handleVolumeChange = useCallback((volume: number) => {
    setLocalVolume(volume);

    // Clear previous timeout
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current);
    }

    // Debounce: only send after 300ms of no changes
    volumeTimeoutRef.current = setTimeout(() => {
      sendCommand('setVolume', { volume });
    }, 300);
  }, [selectedSession]);

  const handleMoodSelect = async (profileSlug: string, mood: string) => {
    await sendCommand('setMood', { profileSlug, mood });
    setShowMoodPicker(false);
  };

  const formatMoodName = (mood: string): string => {
    return mood.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Session list view
  if (!selectedSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800">
        {/* Header */}
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-dark-900/80 border-b border-white/10">
          <div className="max-w-lg mx-auto px-6 py-4">
            <img
              src="/landing/logo-light.svg"
              alt="Fonorex"
              className="h-6 cursor-pointer"
              onClick={() => navigate('/home')}
            />
          </div>
        </header>

        <div className="max-w-lg mx-auto p-6">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('tv.remote', 'TV Remote')}
          </h1>
          <p className="text-gray-400 mb-8">
            {t('tv.selectTv', 'Select a TV to control')}
          </p>

          {sessions.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <p className="text-gray-400 mb-4">
                {t('tv.noSessions', 'No active TV sessions found')}
              </p>
              <p className="text-sm text-gray-500">
                {t('tv.openTvApp', 'Open fonorex.com/tv/app on your TV to start')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <button
                  key={session.sessionCode}
                  onClick={() => connectToSession(session)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-left hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xl font-mono text-primary-400">
                      {session.sessionCode}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        session.isOnline
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {session.isOnline ? t('tv.online', 'Online') : t('tv.offline', 'Offline')}
                    </span>
                  </div>
                  <p className="text-white">{session.deviceName || 'TV'}</p>
                  {session.currentTrackName && (
                    <p className="text-sm text-gray-400 mt-2">
                      {session.isPlaying ? '▶' : '⏸'} {session.currentTrackName}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={loadSessions}
            className="w-full mt-6 py-3 text-gray-400 hover:text-white transition-colors"
          >
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>
    );
  }

  // Remote control view
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-dark-900/80 border-b border-white/10">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={disconnectFromSession}
            className="text-gray-400 hover:text-white flex items-center gap-2"
          >
            ← {t('tv.back', 'Back')}
          </button>
          <img
            src="/landing/logo-light.svg"
            alt="Fonorex"
            className="h-6 cursor-pointer"
            onClick={() => navigate('/home')}
          />
        </div>
      </header>

      <div className="max-w-lg mx-auto p-6">
        {/* Session code */}
        <div className="text-center mb-6">
          <span className="text-primary-400 font-mono text-lg">{selectedSession.sessionCode}</span>
          <p className="text-sm text-gray-500">{selectedSession.deviceName || 'TV'}</p>
        </div>

        {/* Offline Banner */}
        {tvState && !tvState.isOnline && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 text-center">
            <p className="text-yellow-400 font-medium">
              {t('tv.offline', 'TV is offline')}
            </p>
            <p className="text-yellow-400/70 text-sm mt-1">
              {t('tv.waitingForConnection', 'Waiting for TV to reconnect...')}
            </p>
          </div>
        )}

        {/* Now Playing */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          {tvState?.currentTrackName ? (
            <>
              <p className="text-sm text-primary-400 mb-1">
                {t('radio.nowPlaying', 'Now Playing')}
              </p>
              <h2 className="text-xl font-semibold text-white mb-4">
                {tvState.currentTrackName}
              </h2>

              {/* Progress */}
              <div className="mb-4">
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{
                      width: `${tvState.duration > 0 ? (tvState.elapsed / tvState.duration) * 100 : 0}%`,
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-400">
                  <span>{formatTime(tvState.elapsed)}</span>
                  <span>{formatTime(tvState.duration)}</span>
                </div>
              </div>

              {/* Profile & Mood info */}
              <div className="text-sm text-gray-400">
                {tvState.profileSlug && (
                  <span>
                    {profiles.find(p => p.slug === tvState.profileSlug)?.icon}{' '}
                    {profiles.find(p => p.slug === tvState.profileSlug)?.displayName}
                    {tvState.mood && ' · '}
                  </span>
                )}
                {tvState.mood && formatMoodName(tvState.mood)}
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-center py-4">
              {t('tv.notPlaying', 'Not playing')}
            </p>
          )}
        </div>

        {/* Play/Pause Controls */}
        <div className="flex justify-center gap-6 mb-8">
          <button
            onClick={tvState?.isPlaying ? handlePause : handlePlay}
            disabled={!tvState?.isOnline}
            className={`w-20 h-20 rounded-full flex items-center justify-center text-white shadow-lg transition-all ${
              tvState?.isOnline
                ? 'bg-gradient-to-r from-primary-500 to-secondary-500 shadow-primary-500/30'
                : 'bg-gray-600 cursor-not-allowed opacity-50'
            }`}
          >
            {tvState?.isPlaying ? (
              <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-10 h-10 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* Volume Control */}
        <div className={`bg-white/5 border border-white/10 rounded-2xl p-6 mb-6 ${!tvState?.isOnline ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-4">
            <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
            </svg>
            <input
              type="range"
              min="0"
              max="100"
              value={localVolume}
              onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
              disabled={!tvState?.isOnline}
              className={`flex-1 h-2 bg-white/10 rounded-full appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white ${
                tvState?.isOnline ? 'cursor-pointer' : 'cursor-not-allowed'
              }`}
            />
            <span className="text-gray-400 w-12 text-right">{localVolume}%</span>
          </div>
        </div>

        {/* Visualizer Toggle */}
        <button
          onClick={() => sendCommand('toggleVisualizer')}
          disabled={!tvState?.isOnline}
          className={`w-full bg-white/5 border border-white/10 rounded-2xl p-4 mb-4 text-white transition-colors flex items-center justify-center gap-3 ${
            tvState?.isOnline ? 'hover:bg-white/10' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          {t('tv.toggleVisualizer', 'Toggle Visualizer')}
        </button>

        {/* Change Mood Button */}
        <button
          onClick={() => {
            // Initialize with current TV state when opening modal
            const currentProfile = tvState?.profileSlug || '';
            setSelectedProfile(currentProfile);
            setMoods([]);
            if (currentProfile) {
              loadMoods(currentProfile);
            }
            setShowMoodPicker(true);
          }}
          disabled={!tvState?.isOnline}
          className={`w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white transition-colors ${
            tvState?.isOnline ? 'hover:bg-white/10' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          {t('tv.changeMood', 'Change Mood')}
        </button>

        {/* Mood Picker Modal */}
        {showMoodPicker && (
          <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-dark-900 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-white/10 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">
                  {t('tv.selectMood', 'Select mood')}
                </h3>
                <button
                  onClick={() => setShowMoodPicker(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {/* Profile selector - step 1 */}
                {!selectedProfile && (
                  <>
                    <p className="text-sm text-gray-400 mb-3">
                      {t('tv.selectProfile', 'Select your business type')}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {profiles.map((profile) => (
                        <button
                          key={profile._id}
                          onClick={() => {
                            setSelectedProfile(profile.slug);
                            setMoods([]);
                            loadMoods(profile.slug);
                          }}
                          className="p-3 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-colors text-left"
                        >
                          <span className="text-xl mr-2">{profile.icon}</span>
                          <span className="text-sm">{profile.displayName}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* Mood selector - step 2 */}
                {selectedProfile && (
                  <>
                    {/* Selected profile header with back button */}
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        onClick={() => {
                          setSelectedProfile('');
                          setMoods([]);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        ←
                      </button>
                      <span className="text-white font-medium">
                        {profiles.find(p => p.slug === selectedProfile)?.icon}{' '}
                        {profiles.find(p => p.slug === selectedProfile)?.displayName}
                      </span>
                    </div>

                    {/* Loading moods */}
                    {loadingMoods && (
                      <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                      </div>
                    )}

                    {/* Moods grid */}
                    {!loadingMoods && moods.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {moods.map((mood) => (
                          <button
                            key={mood}
                            onClick={() => handleMoodSelect(selectedProfile, mood)}
                            className="p-4 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-primary-500 transition-colors"
                          >
                            {formatMoodName(mood)}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
